/**
 * Auth Slice
 *
 * Zustand slice for user authentication state management.
 * Handles user login/logout, token management, and subscription status.
 * Persists auth state to localStorage (remember me) or sessionStorage (session only).
 *
 * @validates Requirements 8.7, 9.6
 */

import type { StateCreator } from 'zustand';
import { SubscriptionType, UserRole } from '@spellion/shared';
import type { StoreState, AuthSlice, UserState } from './types';

// Storage key for persisting auth state
const AUTH_STORAGE_KEY = 'spellion_auth';
// Key to track which storage type is being used
const AUTH_STORAGE_TYPE_KEY = 'spellion_auth_type';

/**
 * Initial auth state
 */
const initialAuthState: UserState = {
  user: null,
  token: null,
  isAuthenticated: false,
  subscriptionType: null,
  isLoading: false,
  error: null,
};

/**
 * Get the appropriate storage based on remember me preference
 */
function getStorage(): Storage {
  // Check if we previously stored in localStorage (remember me was checked)
  const storageType = localStorage.getItem(AUTH_STORAGE_TYPE_KEY);
  if (storageType === 'local') {
    return localStorage;
  }
  // Default to sessionStorage (or if explicitly set to session)
  return sessionStorage;
}

/**
 * Load auth state from storage (checks both localStorage and sessionStorage)
 */
export function loadAuthFromStorage(): Partial<UserState> {
  try {
    // First check localStorage (remember me)
    let stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        user: parsed.user || null,
        token: parsed.token || null,
        isAuthenticated: !!parsed.token && !!parsed.user,
        subscriptionType: parsed.subscriptionType || null,
      };
    }
    
    // Then check sessionStorage (session only)
    stored = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        user: parsed.user || null,
        token: parsed.token || null,
        isAuthenticated: !!parsed.token && !!parsed.user,
        subscriptionType: parsed.subscriptionType || null,
      };
    }
  } catch (error) {
    console.error('Failed to load auth state from storage:', error);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }
  return {};
}

/**
 * Save auth state to the appropriate storage
 * @param state - Auth state to save
 * @param rememberMe - If true, use localStorage (persists); if false, use sessionStorage (session only)
 */
function saveAuthToStorage(state: Partial<UserState>, rememberMe?: boolean): void {
  try {
    const toStore = {
      user: state.user,
      token: state.token,
      subscriptionType: state.subscriptionType,
    };
    
    // Determine which storage to use
    if (rememberMe !== undefined) {
      // Clear both storages first to avoid conflicts
      localStorage.removeItem(AUTH_STORAGE_KEY);
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
      
      if (rememberMe) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(toStore));
        localStorage.setItem(AUTH_STORAGE_TYPE_KEY, 'local');
      } else {
        sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(toStore));
        localStorage.setItem(AUTH_STORAGE_TYPE_KEY, 'session');
      }
    } else {
      // Update existing storage (for setUser, setToken calls)
      const storage = getStorage();
      storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(toStore));
    }
  } catch (error) {
    console.error('Failed to save auth state to storage:', error);
  }
}

/**
 * Clear auth state from both storages
 */
function clearAuthFromStorage(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_TYPE_KEY);
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear auth state from storage:', error);
  }
}

/**
 * Create the auth slice for the Zustand store
 */
export const createAuthSlice: StateCreator<StoreState, [], [], AuthSlice> = (
  set,
  get
) => ({
  // Initial state (will be hydrated from localStorage)
  ...initialAuthState,

  // Actions
  setUser: (user) => {
    const subscriptionType = user?.subscriptionType || null;
    set({ user, subscriptionType });
    saveAuthToStorage({ ...get(), user, subscriptionType });
  },

  setToken: (token) => {
    const isAuthenticated = !!token && !!get().user;
    set({ token, isAuthenticated });
    saveAuthToStorage({ ...get(), token });
  },

  login: (user, token, rememberMe = false) => {
    const subscriptionType = user.subscriptionType;
    set({
      user,
      token,
      isAuthenticated: true,
      subscriptionType,
      error: null,
    });
    saveAuthToStorage({ user, token, subscriptionType }, rememberMe);
  },

  logout: () => {
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      subscriptionType: null,
      error: null,
    });
    clearAuthFromStorage();
  },

  setSubscriptionType: (subscriptionType) => {
    set({ subscriptionType });
    saveAuthToStorage({ ...get(), subscriptionType });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  isPremium: () => {
    const { subscriptionType, user } = get();
    return (
      subscriptionType === SubscriptionType.PREMIUM ||
      user?.role === UserRole.ADMIN
    );
  },

  isAdmin: () => {
    const { user } = get();
    return user?.role === UserRole.ADMIN;
  },
});
